import { CurrentUserContext } from '@/contexts/contexts';
import { useTwilioPhone } from '@/hooks/useTwilioPhone';
import { useFeatureFlagEnabled } from '@/hooks/useFeatureFlag';
import { alert } from '@/services/alert';
import { formatPhoneNumber } from '@bariendo/lib/phone-validations';
import { FEATURE_FLAGS } from '@bariendo/lib/constants';
import {
  ConferenceParticipantRole,
  ConferenceParticipantStatus,
  ConferencePhase,
  canMergeConsult,
  formatConferenceParticipantDisplayLabel,
  getRemovableProviderParticipants,
  isConsultGuestDialing,
  isGuestParticipant,
  isPrimaryParticipantConnected,
  type ConferenceStartAddTarget,
} from '@bariendo/lib/twilio-conference-session';
import { createReference, getReferenceString } from '@medplum/core';
import { ActionIcon, Box, Button, Group, Stack, Text, ThemeIcon, rem } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconArrowsJoin,
  IconDialpad,
  IconPhone,
  IconPhoneIncoming,
  IconPhoneX,
  IconUserPlus,
  IconX,
} from '@tabler/icons-react';
import { ReactElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { AddPersonToCallModal } from './AddPersonToCallModal';
import { InCallDialpad } from './InCallDialpad';
import { PhoneHangupIcon } from './icons/PhoneHangupIcon';

export function GlobalCallNotification(): ReactElement | null {
  const { currentUser } = useContext(CurrentUserContext) ?? { currentUser: undefined };
  const currentPractitionerRef = currentUser ? getReferenceString(createReference(currentUser)) : undefined;

  const {
    incomingCall,
    outgoingCall,
    acceptCall,
    rejectCall,
    hangupCall,
    currentCall,
    activeCallUi,
    isRemotePartyConnected,
    callDuration,
    conferenceSession,
    conferenceSessionError,
    canManageConference,
    startConferenceAdd,
    reconnectPrimaryConference,
    dropConferenceParticipant,
    mergeConferenceCall,
    cancelConferenceConsult,
  } = useTwilioPhone();

  const isMultiPartyCallingEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_MULTI_PARTY_CALLING);

  const [addDigits, setAddDigits] = useState('');
  const [addModalOpened, { open: openAddModal, close: closeAddModal }] = useDisclosure(false);
  const patientLeftNotifiedRef = useRef(false);

  const handleCloseAddModal = useCallback(() => {
    closeAddModal();
    setAddDigits('');
  }, [closeAddModal]);

  const [keypadOpen, setKeypadOpen] = useState(false);
  const [dtmfSequence, setDtmfSequence] = useState('');

  useEffect(() => {
    if (!currentCall && !activeCallUi) {
      setKeypadOpen(false);
      setDtmfSequence('');
    }
  }, [currentCall, activeCallUi]);

  const isOnActiveCall = Boolean(currentCall || activeCallUi);
  const isPatientOnCall = conferenceSession ? isPrimaryParticipantConnected(conferenceSession) : true;

  useEffect(() => {
    if (!conferenceSession || isPatientOnCall) {
      if (isPatientOnCall) {
        patientLeftNotifiedRef.current = false;
      }
      return;
    }
    if (patientLeftNotifiedRef.current) {
      return;
    }
    patientLeftNotifiedRef.current = true;
    alert.info('Patient left the call.');
  }, [conferenceSession, isPatientOnCall]);

  const draggableRef = useRef(null);

  const getAbbreviatedName = (fullName: string): string => {
    const parts = fullName.trim().split(/\s+/);

    if (parts.length <= 2) {
      return fullName;
    }

    const [firstName, ...rest] = parts;
    const lastName = rest.pop()!;
    const middleInitials = rest.map((name) => name.charAt(0).toUpperCase() + '.').join(' ');

    return `${firstName} ${middleInitials} ${lastName}`;
  };

  const callInfo = useMemo(() => {
    const isIncoming = Boolean(incomingCall) || activeCallUi?.direction === 'incoming';
    const call = incomingCall || outgoingCall;
    const rawName = call?.callerName?.trim() ?? activeCallUi?.callerName?.trim();
    const callerOrganization = call?.callerOrganization ?? activeCallUi?.callerOrganization;
    const callerName = callerOrganization
      ? callerOrganization
      : rawName
        ? getAbbreviatedName(rawName)
        : 'Unknown caller';
    const phoneNumber = isIncoming
      ? (incomingCall?.from ?? activeCallUi?.phoneNumber)
      : (outgoingCall?.to ?? activeCallUi?.phoneNumber);

    return {
      isIncoming,
      callerName,
      callerOrganization,
      phoneNumber,
      formattedNumber: phoneNumber ? formatPhoneNumber(phoneNumber) : undefined,
      label: isIncoming ? 'Incoming call' : 'Outgoing call',
    };
  }, [incomingCall, outgoingCall, activeCallUi]);

  const canAddPerson = useMemo(() => {
    if (conferenceSession?.phase === ConferencePhase.CONSULT) {
      return false;
    }
    if (conferenceSession?.phase === ConferencePhase.ACTIVE) {
      return true;
    }
    return isRemotePartyConnected;
  }, [conferenceSession, isRemotePartyConnected]);

  const showMergeButton = useMemo(
    () => canMergeConsult(conferenceSession ?? undefined) && !isConsultGuestDialing(conferenceSession ?? undefined),
    [conferenceSession]
  );

  const isPatientOnHoldForConsult = conferenceSession?.phase === ConferencePhase.CONSULT;

  const removableParticipants = useMemo(() => {
    if (!conferenceSession || !canManageConference) {
      return [];
    }
    return getRemovableProviderParticipants(conferenceSession).filter(
      (participant) => !participant.providerReference || participant.providerReference !== currentPractitionerRef
    );
  }, [conferenceSession, canManageConference, currentPractitionerRef]);

  const connectedParticipants = useMemo(() => {
    if (!conferenceSession) {
      return [];
    }
    return conferenceSession.participants.filter(
      (p) =>
        (p.role === ConferenceParticipantRole.AGENT ||
          p.role === ConferenceParticipantRole.PRIMARY ||
          p.role === ConferenceParticipantRole.GUEST) &&
        p.status !== ConferenceParticipantStatus.DISCONNECTED
    );
  }, [conferenceSession]);

  const viewerIsGuest = useMemo(() => {
    if (!conferenceSession || !currentPractitionerRef) {
      return false;
    }
    return conferenceSession.participants.some((p) => p.providerReference === currentPractitionerRef);
  }, [conferenceSession, currentPractitionerRef]);

  const viewerIsConsultGuest = useMemo(() => {
    if (!conferenceSession || !currentPractitionerRef) {
      return false;
    }
    return conferenceSession.participants.some(
      (p) => isGuestParticipant(p) && p.providerReference === currentPractitionerRef
    );
  }, [conferenceSession, currentPractitionerRef]);

  const showCancelConsultButton = useMemo(() => {
    if (!conferenceSession || conferenceSession.phase !== ConferencePhase.CONSULT) {
      return false;
    }
    if (viewerIsConsultGuest) {
      return false;
    }
    return isConsultGuestDialing(conferenceSession) || !canMergeConsult(conferenceSession);
  }, [conferenceSession, viewerIsConsultGuest]);

  const participantDisplayContext = useMemo(
    () => ({
      patientDisplayName:
        callInfo.callerName && callInfo.callerName !== 'Unknown caller' ? callInfo.callerName : undefined,
      currentProviderReference: currentPractitionerRef,
      viewerIsGuest,
    }),
    [callInfo.callerName, currentPractitionerRef, viewerIsGuest]
  );

  const formatParticipantLabel = useCallback(
    (participant: Parameters<typeof formatConferenceParticipantDisplayLabel>[0]) =>
      formatConferenceParticipantDisplayLabel(participant, participantDisplayContext),
    [participantDisplayContext]
  );

  const sendDigit = useCallback(
    (digit: string) => {
      currentCall?.sendDigits(digit);
      setDtmfSequence((prev) => prev + digit);
    },
    [currentCall]
  );

  const handleEnteredDigitsClear = useCallback(() => {
    setDtmfSequence('');
  }, []);

  if (!incomingCall && !outgoingCall && !currentCall && !activeCallUi) {
    return null;
  }

  const handleHangup = () => {
    if (currentCall || activeCallUi) {
      hangupCall();
    } else {
      rejectCall();
    }
  };

  const handleAddSubmit = (target: ConferenceStartAddTarget): void => {
    if (target.type === 'phone') {
      const trimmed = target.toPhone.trim();
      const digits = trimmed.replace(/\D/g, '');
      if (digits.length < 10) {
        alert.error('Enter a 10-digit phone number.');
        return;
      }
    }
    handleCloseAddModal();
    void startConferenceAdd(target);
  };

  const renderCallButtons = () => {
    return (
      <>
        {currentCall ? (
          <ActionIcon
            radius="xl"
            bg="#344a55"
            size="lg"
            onClick={(e) => {
              e.stopPropagation();
              setKeypadOpen((open) => !open);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            aria-label={keypadOpen ? 'Hide keypad' : 'Show keypad'}
            aria-pressed={keypadOpen}
            style={{ pointerEvents: 'auto' }}
          >
            <IconDialpad size={18} color="white" />
          </ActionIcon>
        ) : null}

        <ActionIcon
          radius="xl"
          bg="red.6"
          size="lg"
          mx={8}
          onClick={(e) => {
            e.stopPropagation();
            handleHangup();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          aria-label="Hang up call"
          style={{ pointerEvents: 'auto' }}
        >
          <PhoneHangupIcon height={18} width={18} />
        </ActionIcon>
      </>
    );
  };

  const renderReceivingCallButtons = () => {
    return (
      <Group gap="xs">
        <Button
          size="xs"
          bg="green"
          c="white"
          variant="white"
          leftSection={<IconPhone size={14} />}
          onClick={(e) => {
            e.stopPropagation();
            acceptCall();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          radius="xl"
          aria-label="Answer call"
          style={{ pointerEvents: 'auto' }}
        >
          Answer
        </Button>

        <Button
          size="xs"
          bg="red"
          variant="outline"
          leftSection={<IconPhoneX size={14} />}
          onClick={(e) => {
            e.stopPropagation();
            rejectCall();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          c="white"
          radius="xl"
          aria-label="Decline call"
          style={{ pointerEvents: 'auto' }}
        >
          Decline
        </Button>
      </Group>
    );
  };

  return (
    <>
      <Draggable handle=".drag-handle" bounds="body" nodeRef={draggableRef}>
        <Box
          style={{
            position: 'fixed',
            top: rem(70),
            right: rem(20),
            zIndex: 1000,
            backgroundColor: '#243944',
            borderRadius: rem(32),
            padding: rem(12),
            minWidth: rem(400),
            maxWidth: rem(600),
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            transition: 'background-color 0.2s ease',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
          }}
        >
          <Stack gap={0}>
            <Group
              gap="sm"
              align="center"
              justify="space-between"
              wrap="nowrap"
              className="drag-handle"
              style={{
                cursor: 'grab',
                transition: 'background-color 0.2s ease',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none',
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.cursor = 'grabbing';
                e.preventDefault();
              }}
              onMouseUp={(e) => (e.currentTarget.style.cursor = 'grab')}
              onDragStart={(e) => e.preventDefault()}
              ref={draggableRef}
            >
              <Group gap="sm" align="center" wrap="nowrap">
                <ThemeIcon size="lg" radius="xl" bg="#344a55">
                  <IconPhoneIncoming size={18} />
                </ThemeIcon>
                <Box>
                  <Text size="xs" c="gray.4">
                    {callInfo.label}
                  </Text>
                  <Text size="sm" c="white" fw={400}>
                    {callInfo.callerOrganization && (
                      <Text span fw={600} mr={'xs'}>
                        {callInfo.callerOrganization}
                      </Text>
                    )}
                    {callInfo.callerName}
                    {callInfo.formattedNumber && (
                      <Text span fw={600} mx={8}>
                        {callInfo.formattedNumber}
                      </Text>
                    )}
                  </Text>
                </Box>
              </Group>

              <Group gap="xs" ml="auto">
                <Text size="xs" c="white">
                  {callDuration}
                </Text>

                {isOnActiveCall ? renderCallButtons() : renderReceivingCallButtons()}
              </Group>
            </Group>

            {currentCall && dtmfSequence && !keypadOpen ? (
              <Text
                size="xs"
                c="gray.4"
                mt="xs"
                px="xs"
                style={{ fontFamily: 'monospace', letterSpacing: rem(4) }}
                aria-live="polite"
              >
                {dtmfSequence}
              </Text>
            ) : null}

            {currentCall && keypadOpen ? (
              <Box onMouseDown={(e) => e.stopPropagation()}>
                <InCallDialpad
                  enteredDigits={dtmfSequence}
                  onEnteredDigitsClear={handleEnteredDigitsClear}
                  onDigit={sendDigit}
                />
              </Box>
            ) : null}

            {isMultiPartyCallingEnabled && isOnActiveCall && (
              <Stack gap="xs" mt="sm" pl="xs" pr="xs" pb="xs">
                {conferenceSession && !isPatientOnCall && (
                  <Text size="xs" c="orange.3">
                    Patient left the call
                  </Text>
                )}
                {conferenceSessionError && (
                  <Text size="xs" c="red.3">
                    {conferenceSessionError}
                  </Text>
                )}
                {isPatientOnHoldForConsult && (
                  <Text size="xs" c="yellow.3">
                    Patient is on hold while you consult with the added person.
                  </Text>
                )}
                {canManageConference && (
                  <Group gap="xs" wrap="wrap">
                    {showMergeButton && (
                      <Button
                        size="xs"
                        variant="light"
                        color="blue"
                        leftSection={<IconArrowsJoin size={14} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          void mergeConferenceCall();
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        Merge
                      </Button>
                    )}
                    {showCancelConsultButton && (
                      <Button
                        size="xs"
                        variant="light"
                        color="orange"
                        leftSection={<IconX size={14} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          void cancelConferenceConsult();
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        Cancel add
                      </Button>
                    )}
                    {conferenceSession && !isPatientOnCall && (
                      <Button
                        size="xs"
                        variant="light"
                        color="green"
                        leftSection={<IconPhone size={14} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          void reconnectPrimaryConference();
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        Call patient back
                      </Button>
                    )}
                    <Button
                      size="xs"
                      variant="light"
                      color={!canAddPerson ? 'black' : 'green'}
                      bg={!canAddPerson ? 'gray.2' : 'gray.1'}
                      leftSection={<IconUserPlus size={14} />}
                      disabled={!canAddPerson}
                      onClick={(e) => {
                        e.stopPropagation();
                        openAddModal();
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      Add person
                    </Button>
                  </Group>
                )}

                {conferenceSession && connectedParticipants.length > 0 && (
                  <Stack gap={4}>
                    {removableParticipants.length > 0 && (
                      <Group gap="xs" wrap="wrap">
                        {removableParticipants.map((participant) => (
                          <Button
                            key={participant.callSid ?? participant.label}
                            size="xs"
                            variant="light"
                            color="orange"
                            disabled={!participant.callSid}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (participant.callSid) {
                                void dropConferenceParticipant(participant.callSid);
                              }
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            Remove {formatParticipantLabel(participant)}
                          </Button>
                        ))}
                      </Group>
                    )}
                  </Stack>
                )}
              </Stack>
            )}
          </Stack>
        </Box>
      </Draggable>

      <AddPersonToCallModal
        opened={addModalOpened}
        onClose={handleCloseAddModal}
        phoneNumber={addDigits}
        onPhoneNumberChange={setAddDigits}
        onDial={handleAddSubmit}
      />
    </>
  );
}
